import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { money, dims, CATEGORY_LABEL } from "@/lib/api";
import { WaitlistForm } from "@/components/site/WaitlistForm";

const VariantPicker = ({ variants, value, onChange }) => (
  <div className="mt-10">
    <p className="field-label mb-4">Size</p>
    <div className="flex flex-wrap gap-3" data-testid="product-size-select">
      {variants.map((v, i) => (
        <button
          key={v.label + i}
          data-testid={`product-size-option-${i}`}
          onClick={() => onChange(i)}
          className={`pill ${i === value ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FDFBF7]" : "border-[#D9D3C7] text-[#3A3733] hover:border-[#1A1A1A]"}`}
        >
          {v.label}
        </button>
      ))}
    </div>
  </div>
);

const PRINT_SPECS = {
  "Canvas Print": {
    heading: "Description",
    intro: "Enhanced texture and timeless beauty with our canvas print. The canvas texture enhances the image's natural look and feel, creating a truly immersive art experience:",
    specs: [
      ["Canvas Material", "Responsibly sourced FSC-certified wood stretcher bars, cotton-polyester blend (300-350gsm, 350-400 microns)."],
      ["Thickness", "Slim (2cm) and Thick (4cm) options."],
    ],
    note: "Canvas prints are unframed.",
  },
  "Fine Art Poster": {
    specs: [
      ["Color Vibrancy", "Our 12-color fine art printing technology delivers vivid, accurate colors with stunning depth, far beyond standard 4-color inkjet printing technology."],
      ["Paper Finishing", "Smooth matte finish for a clean, glare-free display."],
      ["Paper Weight", "200 gsm (80 lb), thickness: 0.26 mm (10.3 mils)."],
    ],
    note: "Note: Prints ship unframed.",
  },
};

const PrintSpecs = ({ spec }) => (
  <div className="mt-10 border-t border-[#E5E0D8] pt-8 text-sm leading-[1.8] text-[#3A3733]" data-testid="product-print-specs">
    {spec.heading && <p className="field-label mb-4">{spec.heading}</p>}
    {spec.intro && <p className="mb-4">{spec.intro}</p>}
    <ul className="space-y-2">
      {spec.specs.map(([k, v]) => (
        <li key={k}><span className="font-medium text-[#1A1A1A]">{k}:</span> {v}</li>
      ))}
    </ul>
    <p className="mt-4 text-xs text-[#5A626A]" data-testid="product-print-unframed-note">{spec.note}</p>
  </div>
);

export const ProductInfo = ({ p, original, onVariant }) => {
  const { add } = useCart();
  const [vi, setVi] = useState(0);
  const [touched, setTouched] = useState(false);
  const variant = p.variants?.[vi];
  const choose = (i) => {
    setVi(i);
    setTouched(true);
    onVariant?.(p.variants[i]);
  };
  const origDims = dims(original || p);
  const medium = p.medium || original?.medium;
  const sizeLabel = !p.isOriginal && touched && variant
    ? `Selected Print Size: ${variant.label}${origDims ? ` (Original: ${origDims})` : ""}`
    : origDims ? `Original Artwork Size: ${origDims}` : "";
  const price = p.isOriginal ? p.price : variant?.price;
  const paragraphs = useMemo(() => p.description.split(/\n+/).filter(Boolean), [p.description]);

  const addToBag = () => {
    add({
      key: p.isOriginal ? p.id : `${p.id}-${vi}`, productId: p.id, slug: p.slug, title: p.title, category: p.category,
      isOriginal: p.isOriginal, variantIndex: p.isOriginal ? null : vi, variantLabel: p.isOriginal ? "Original painting" : variant.label,
      price, image: p.images?.[0],
    });
    toast.success(`${p.title} added to your bag`);
  };

  return (
    <div className="lg:sticky lg:top-28" data-testid="product-info">
      <p className="eyebrow">{CATEGORY_LABEL[p.category]}</p>
      <h1 className="mt-5 font-display text-5xl font-light leading-[1.05] sm:text-6xl" data-testid="product-title">{p.title}</h1>
      {medium && <p className="mt-5 text-sm text-[#5A626A]" data-testid="product-medium">{medium}</p>}
      {sizeLabel && <p className={`${medium ? "mt-1" : "mt-5"} text-sm text-[#5A626A]`} data-testid="product-size-label">{sizeLabel}</p>}
      <p className="mt-8 font-display text-3xl" data-testid="product-price">{p.isSold ? "Sold" : money(price)}</p>
      {!p.isOriginal && p.variants?.length > 0 && <VariantPicker variants={p.variants} value={vi} onChange={choose} />}
      <button className="btn-primary mt-10 w-full" disabled={p.isSold} onClick={addToBag} data-testid="add-to-cart-button">
        {p.isSold ? "This painting has found a home" : "Add to bag"}
      </button>
      {p.isOriginal && p.isSold && (
        <div className="mt-8">
          <WaitlistForm productId={p.id} title="Join the waitlist" text="This painting has sold. Leave your email and you'll be the first to hear when a similar original is finished." testid="product-waitlist" />
        </div>
      )}
      {p.isOriginal && p.framingDisclaimer && (
        <p className="mt-6 border-l border-[#8C7A5B] pl-4 text-xs leading-relaxed text-[#5A626A]" data-testid="product-framing-disclaimer">{p.framingDisclaimer}</p>
      )}
      <div className="mt-12 space-y-5 text-[15px] leading-[1.8] text-[#3A3733]" data-testid="product-description">
        {paragraphs.map((t, i) => <p key={i}>{t}</p>)}
      </div>
      {PRINT_SPECS[p.category] && <PrintSpecs spec={PRINT_SPECS[p.category]} />}
      <div className="mt-12 border-t border-[#E5E0D8] pt-8 text-xs leading-relaxed text-[#5A626A]">
        {p.isOriginal
          ? "Free shipping within Australia — 2–3 business days custom packing, then 2–6 business days transit. Insured tiered shipping internationally."
          : "Printed to order — 2–5 business days production, then 3–5 business days courier transit worldwide."}{" "}
        <Link to="/faq" className="underline underline-offset-4" data-testid="product-faq-link">Shipping & returns</Link>
      </div>
    </div>
  );
};
